import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { RequireAuth } from 'grabbitbot-legacy-ui'
import { Shell } from '../layout/Shell'
import { ADMIN_ROLE } from '../config'

import { LoginPage } from '../pages/auth/LoginPage'
import { RegisterPage } from '../pages/auth/RegisterPage'
import { LogoutPage } from '../pages/auth/LogoutPage'
import { PasswordForgotPage } from '../pages/auth/PasswordForgotPage'
import { PasswordForgotSetPage } from '../pages/auth/PasswordForgotSetPage'
import { RegisterValidatePage } from '../pages/auth/RegisterValidatePage'
import { PasswordSetupValidatePage } from '../pages/auth/PasswordSetupValidatePage'


import { CoinsPage } from '../pages/trading/CoinsPage'
import { SettingsDefaultPage } from '../pages/trading/SettingsDefaultPage'
import { SettingsExchangePage } from '../pages/trading/SettingsExchangePage'
import { StrategySearchPage } from '../pages/trading/StrategySearchPage'
import { StrategyListPage } from '../pages/trading/StrategyListPage'
import { StrategySheetPage } from '../pages/trading/StrategySheetPage'

import { OpenPositionsPage } from '../pages/statistics/OpenPositionsPage'
import { ClosedPositionsPage } from '../pages/statistics/ClosedPositionsPage'
import { ClosedPositionsSearchPage } from '../pages/statistics/ClosedPositionsSearchPage'
import { GlobalCoinSummaryPage } from '../pages/statistics/GlobalCoinSummaryPage'
import { StuckCoinsPage } from '../pages/statistics/StuckCoinsPage'
import { TransactionsPage } from '../pages/statistics/TransactionsPage'

import { ServiceAccountsPage } from '../pages/account/ServiceAccountsPage'
import { UserPage } from '../pages/account/UserPage'

import { PaymentHomePage } from '../pages/payment/PaymentHomePage'
import { PlanListPage } from '../pages/payment/PlanListPage'
import { ProductListPage } from '../pages/payment/ProductListPage'
import { UsageFeeListPage } from '../pages/payment/UsageFeeListPage'
import { PaymentSuccessPage } from '../pages/payment/PaymentSuccessPage'
import { PaymentCancelPage } from '../pages/payment/PaymentCancelPage'
import { PaymentMethodPage } from '../pages/payment/PaymentMethodPage'
import { PaymentBillingPage } from '../pages/payment/PaymentBillingPage'
import { PaymentCreditPage } from '../pages/payment/PaymentCreditPage'
import { PaymentTransactionsPage } from '../pages/payment/PaymentTransactionsPage'
import { PaymentInvoicesPage } from '../pages/payment/PaymentInvoicesPage'

import { AdminLogPage } from '../pages/admin/AdminLogPage'
import { AdminProfitStatisticsPage } from '../pages/admin/AdminProfitStatisticsPage'
import { AdminUserLifetimeProfitPage } from '../pages/admin/AdminUserLifetimeProfitPage'
import { AdminPaymentDashboardPage } from '../pages/admin/AdminPaymentDashboardPage'
import { AdminPaymentUserPage } from '../pages/admin/AdminPaymentUserPage'
import { AdminBotUserPage } from '../pages/admin/AdminBotUserPage'
import { AdminUserLogPage } from '../pages/admin/AdminUserLogPage'
import { AdminStrategyListPage } from '../pages/admin/AdminStrategyListPage'
import { AdminStrategyStatPage } from '../pages/admin/AdminStrategyStatPage'
import { AdminUsersPage } from '../pages/admin/AdminUsersPage'
import { AdminUserCreditPage } from '../pages/admin/AdminUserCreditPage'
import { AdminUserBotStatusPage } from '../pages/admin/AdminUserBotStatusPage'
import { AdminUserStrategyCommissionPage } from '../pages/admin/AdminUserStrategyCommissionPage'
import { AdminUserUsagefeePage } from '../pages/admin/AdminUserUsagefeePage'
import { AdminUserWalletPage } from '../pages/admin/AdminUserWalletPage'

// Chart pages are lazy-loaded so echarts (~1 MB) stays out of the entry chunk (BT 6d445835).
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })))
const CoinSheetPage = lazy(() => import('../pages/trading/CoinSheetPage').then((m) => ({ default: m.CoinSheetPage })))
const CoinTreasuryPage = lazy(() => import('../pages/statistics/CoinTreasuryPage').then((m) => ({ default: m.CoinTreasuryPage })))
const GlobalProfitSummaryPage = lazy(() => import('../pages/statistics/GlobalProfitSummaryPage').then((m) => ({ default: m.GlobalProfitSummaryPage })))

// This is the route-guard fix for legacy-migration audit §3/§10 defect 2
// ("no canActivate guards exist anywhere in the codebase" — every route,
// including /admin/* and every /payment/* page, was reachable by direct URL
// navigation regardless of login state, enforcement existing only — if at
// all — on the unaudited PHP backend). Every protected route below is
// wrapped in `RequireAuth`; `requireRole` additionally closes audit §10
// defect 4 (`User.roles` defined but never read — no admin route was ever
// actually role-checked client-side).
function Protected({ children, requireRole }: { children: ReactNode; requireRole?: string }) {
  return (
    <RequireAuth fallback={<Navigate to="/login" replace />} pending={<p>Loading…</p>} requireRole={requireRole}>
      <Shell>
        <Suspense fallback={<p>Loading…</p>}>{children}</Suspense>
      </Shell>
    </RequireAuth>
  )
}

/**
 * Route inventory — see legacy-migration audit
 * (`docs/audit/components/secure.grabbitbot.com.md` §3a) for the full
 * legacy table this is ported from (65 routes). Round 1 registers every
 * live legacy path with a placeholder page (see
 * `src/pages/PlaceholderPage.tsx`) so later batches only ever swap a
 * component in, never touch routing/guards again. NOT reproduced, with
 * reasons (audit §10 defects, also in README "Known gaps"):
 *
 *  - `payment/old`, `payment/old/success`, `payment/old/cancel` — legacy
 *    dead redirect shim (`PaymentOldComponent.ngOnInit` did an unconditional
 *    `location.href = '/payment'` before any of its own code ran, audit
 *    §3a/§10 defect 12) — kept as an immediate redirect for old
 *    bookmarks/emails, exactly like the legacy behaviour, never wrapped in
 *    `Protected` (the legacy shim redirected unconditionally too).
 *  - `trader/:id`, `trader/:id/edit` — legacy dead feature, components
 *    deleted from the app but the routes redirected to
 *    `/strategy/spot/search` (audit §3a) — same redirect kept here.
 *  - The second, unreachable `{ path: 'payment', redirectTo: ... }` route
 *    entry (audit §10 defect 14, "duplicate route entries") — dropped
 *    entirely, it was dead routing config even in the legacy app (Angular's
 *    router used the first match).
 *  - `admin-dashboard` (audit §3a/§10 defect 11a) — empty template, never
 *    routed in the legacy app either. Not registered here.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* --- public / auth --- */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/password-forgot" element={<PasswordForgotPage />} />
      <Route path="/password-forgot-set" element={<PasswordForgotSetPage />} />
      <Route path="/register-validate" element={<RegisterValidatePage />} />
      <Route path="/passwordsetup-validate" element={<PasswordSetupValidatePage />} />

      {/* --- dashboard --- */}
      <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
      <Route
        path="/:exchangeId/:tradingType/dashboard"
        element={<Protected><DashboardPage /></Protected>}
      />

      {/* --- trading config --- */}
      <Route path="/settings/default" element={<Protected><SettingsDefaultPage /></Protected>} />
      <Route path="/settings/exchange" element={<Protected><SettingsExchangePage /></Protected>} />
      <Route path="/:exchangeId/:tradingType/coins" element={<Protected><CoinsPage /></Protected>} />
      <Route
        path="/:exchangeId/:tradingType/coins/:coin1/:coin2"
        element={<Protected><CoinSheetPage /></Protected>}
      />
      <Route path="/strategy/:tradingType/search" element={<Protected><StrategySearchPage /></Protected>} />
      <Route path="/strategy/:tradingType" element={<Protected><StrategyListPage /></Protected>} />
      <Route path="/strategy/:tradingType/:id" element={<Protected><StrategySheetPage /></Protected>} />

      {/* --- statistics --- */}
      <Route
        path="/:exchangeId/:tradingType/statistics/positions/open"
        element={<Protected><OpenPositionsPage /></Protected>}
      />
      <Route
        path="/:exchangeId/:tradingType/statistics/positions/closed"
        element={<Protected><ClosedPositionsPage /></Protected>}
      />
      <Route
        path="/:exchangeId/:tradingType/statistics/positions/closed/:type"
        element={<Protected><ClosedPositionsSearchPage /></Protected>}
      />
      <Route
        path="/:exchangeId/:tradingType/statistics/global/coin-summary"
        element={<Protected><GlobalCoinSummaryPage /></Protected>}
      />
      <Route
        path="/:exchangeId/:tradingType/statistics/global/profit-summary"
        element={<Protected><GlobalProfitSummaryPage /></Protected>}
      />
      <Route
        path="/:exchangeId/:tradingType/statistics/:coin/treasury"
        element={<Protected><CoinTreasuryPage /></Protected>}
      />
      <Route
        path="/:exchangeId/:tradingType/statistics/stuck-coins"
        element={<Protected><StuckCoinsPage /></Protected>}
      />
      <Route
        path="/:exchangeId/:tradingType/statistics/transactions"
        element={<Protected><TransactionsPage /></Protected>}
      />

      {/* --- account / service accounts --- */}
      <Route path="/user" element={<Protected><UserPage /></Protected>} />
      <Route path="/settings/service-accounts" element={<Protected><ServiceAccountsPage /></Protected>} />
      <Route path="/service-accounts" element={<Protected><ServiceAccountsPage /></Protected>} />

      {/* --- payment (current stack only — audit §10 defect 12: the old
          /payment/old/* stack is superseded, see docblock above) --- */}
      <Route path="/payment/old" element={<Navigate to="/payment" replace />} />
      <Route path="/payment/old/success" element={<Navigate to="/payment" replace />} />
      <Route path="/payment/old/cancel" element={<Navigate to="/payment" replace />} />
      <Route path="/payment" element={<Protected><PaymentHomePage /></Protected>} />
      <Route path="/payment/subscriptions" element={<Protected><PlanListPage /></Protected>} />
      <Route path="/payment/subscriptions/:planId" element={<Protected><PlanListPage /></Protected>} />
      <Route path="/payment/products" element={<Protected><ProductListPage /></Protected>} />
      <Route path="/payment/products/:productId" element={<Protected><ProductListPage /></Protected>} />
      <Route path="/payment/usagefees" element={<Protected><UsageFeeListPage /></Protected>} />
      <Route path="/payment/usagefees/:usagefeeId" element={<Protected><UsageFeeListPage /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccessPage /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancelPage /></Protected>} />
      <Route path="/payment/method" element={<Protected><PaymentMethodPage /></Protected>} />
      <Route path="/payment/billing" element={<Protected><PaymentBillingPage /></Protected>} />
      <Route path="/payment/credit" element={<Protected><PaymentCreditPage /></Protected>} />
      <Route path="/payment/transactions" element={<Protected><PaymentTransactionsPage /></Protected>} />
      <Route path="/payment/invoices" element={<Protected><PaymentInvoicesPage /></Protected>} />

      {/* --- admin (real role guard — fixes audit §10 defects 2 & 4) --- */}
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route path="/admin/log" element={<Protected requireRole={ADMIN_ROLE}><AdminLogPage /></Protected>} />
      <Route
        path="/admin/statistics/yield"
        element={<Protected requireRole={ADMIN_ROLE}><AdminProfitStatisticsPage /></Protected>}
      />
      <Route
        path="/admin/statistics/profit"
        element={<Protected requireRole={ADMIN_ROLE}><AdminProfitStatisticsPage /></Protected>}
      />
      <Route
        path="/admin/statistics/profit/lifetime"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUserLifetimeProfitPage /></Protected>}
      />
      <Route
        path="/admin/payment"
        element={<Protected requireRole={ADMIN_ROLE}><AdminPaymentDashboardPage /></Protected>}
      />
      <Route
        path="/admin/payment/user"
        element={<Protected requireRole={ADMIN_ROLE}><AdminPaymentUserPage /></Protected>}
      />
      <Route
        path="/admin/bot/user"
        element={<Protected requireRole={ADMIN_ROLE}><AdminBotUserPage /></Protected>}
      />
      <Route
        path="/admin/bot/user/status"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUserBotStatusPage /></Protected>}
      />
      <Route
        path="/admin/log/user"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUserLogPage /></Protected>}
      />
      <Route
        path="/admin/strategies"
        element={<Protected requireRole={ADMIN_ROLE}><AdminStrategyListPage /></Protected>}
      />
      <Route
        path="/admin/strategies/:id"
        element={<Protected requireRole={ADMIN_ROLE}><AdminStrategyStatPage /></Protected>}
      />
      <Route path="/admin/users" element={<Protected requireRole={ADMIN_ROLE}><AdminUsersPage /></Protected>} />
      <Route
        path="/admin/users/credit"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUserCreditPage /></Protected>}
      />
      <Route
        path="/admin/users/strategy/commission"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUserStrategyCommissionPage /></Protected>}
      />
      <Route
        path="/admin/users/usagefee"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUserUsagefeePage /></Protected>}
      />
      <Route
        path="/admin/users/wallet"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUserWalletPage /></Protected>}
      />

      {/* --- legacy dead "trader" feature — redirect kept, audit §3a --- */}
      <Route path="/trader/:id" element={<Navigate to="/strategy/spot/search" replace />} />
      <Route path="/trader/:id/edit" element={<Navigate to="/strategy/spot/search" replace />} />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}
